import { t } from 'i18next';

import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { NumberHelper } from 'core/helpers';
import { formatDateForServer } from 'core/utils';
import { CustomerExternalNumberType } from 'customer/enums';
import { Source } from 'parts/enums';
import { PricingHelper } from 'pricing/helpers';
import { ShipSelection } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields/enums';
import {
  CostValidityEntry,
  PriceBasedOn,
  PricingStrategy,
  QuotePartAddToPricingProfileOption,
  QuotePartNoteType,
  QuoteType,
  QuoteVersionType,
  ValidationStatus,
  ValidationType,
} from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import {
  IAddToPricingProfileForm,
  ICalculatePriceForm,
  ICostDiscountForm,
  ICreateOrderForm,
  ICustomerQuotePart,
  IDefaultSpaAgreementFormValue,
  IDefaultSpaAgreementPayload,
  IDiscountPartsFixedPayload,
  ILeadTime,
  IMoveToForm,
  IQuotePart,
  IQuotePartLineForm,
  IQuotePartNote,
  IQuotePartNoteForm,
  IResetPricingForm,
  IResetPricingPayload,
  ISelectedSpaFormValue,
} from 'quotes/types';
import {
  formatCostValidityDateForServer,
  getFormCostValidityEntry,
  getSelectedSpaIdentity,
  hasVendorQuoteValidUntilToClear,
  TOneTimeSpaRow,
} from 'quotes/utils';
import { TaxonomySource } from 'taxonomy/enums';

import { mapDefaultSpaAgreementPayload, mapSelectedSpaCostSource } from './tradeAgreement';

export const mapLockUnlockQuoteParts = (
  quoteParts: ICustomerQuotePart[],
  allPartsLocked = false
) => ({
  quoteParts: quoteParts.map((quotePart) => ({
    id: quotePart.id,
    locked: allPartsLocked ? 0 : 1,
  })),
});

export const mapCreateQuotePartNotesToQuotePartNotes = (
  data: IQuotePartNoteForm,
  leadTime?: ILeadTime
): IQuotePartNote[] => {
  const notes: IQuotePartNote[] = [];

  Object.entries(data.notes).forEach(([type, note]) => {
    if (note.enabled && (note.content || type === QuotePartNoteType.UNDISCARDABLE_ITEM)) {
      notes.push({
        type: type as QuotePartNoteType,
        content:
          type === QuotePartNoteType.UNDISCARDABLE_ITEM
            ? t('labelCreateUndiscardableItemNoteStrong')
            : note.content,
      });
    }
  });

  if (data?.notes?.[QuotePartNoteType.LEAD_TIME]?.hasLeadTime) {
    notes.push({
      type: QuotePartNoteType.LEAD_TIME,
      content: t('labelDayCount', { count: leadTime?.leadTime }),
    });
  }

  return notes;
};

export const mapCreateQuotePartNotesToServer = (
  selectedQuoteParts: IQuotePart[],
  data: IQuotePartNoteForm,
  leadTime?: ILeadTime
) => ({
  quotePartNotes: selectedQuoteParts.flatMap((selectedQuotePart) =>
    mapCreateQuotePartNotesToQuotePartNotes(data, leadTime).map((note) => ({
      quotePartId: selectedQuotePart.id,
      type: note.type,
      content: note.content,
    }))
  ),
});

export const mapValidateQuoteParts = (quoteParts: ICustomerQuotePart[], type: ValidationType) => ({
  quoteParts: quoteParts.map((quotePart) => ({
    id: quotePart.id,
    [type]: ValidationStatus.VALIDATED_BY_USER,
  })),
});

export const mapResetPricingQuoteParts = (
  data: IResetPricingForm,
  quoteParts: ICustomerQuotePart[],
  resetPricingFormPayload: IResetPricingPayload | null = null
) => {
  if (!resetPricingFormPayload) {
    return {
      quoteParts: quoteParts.map((quotePart) => quotePart.id),
      pricingTypes: Object.keys(data).filter((key) => data[key]),
    };
  }

  return {
    pricingTypes: Object.keys(data).filter((key) => data[key]),
    ...resetPricingFormPayload,
  };
};

export const mapQuoteNoQuoteQuoteParts = (
  quoteParts: ICustomerQuotePart[],
  allPartsNoQuote = false
) => ({
  quoteParts: quoteParts.map((quotePart) => ({
    id: quotePart.id,
    noQuote: allPartsNoQuote ? 0 : 1,
  })),
});

export const mapQuotePartsCreateOrder = (
  quoteId: number,
  quoteParts: ICustomerQuotePart[],
  {
    customerNumber,
    customerNumberType,
    orderType,
    purchaseOrderNumber,
    shipSelection,
    earliestAcceptableDate,
    requiredOnSiteDate,
    shippingMethod,
    freightTerm,
    billToLocation,
    shipToLocation,
  }: ICreateOrderForm,
  isSelectionAction = false,
  isAlternateVersionEnabled = false
) => {
  const extra = {};
  if (isSelectionAction) {
    extra['quoteParts'] = {
      ids: quoteParts.map((quotePart) => quotePart.id),
    };
  }

  if (isAlternateVersionEnabled) {
    extra['orderType'] = orderType;
    extra['shipSelection'] = shipSelection;
    // NDD earliest is API-owned (submission day); the BE ignores anything sent.
    extra['earliestAcceptableDate'] =
      shipSelection === ShipSelection.NO_DELIVERY_DATE
        ? ''
        : formatDateForServer(earliestAcceptableDate?.toString(), SERVER_FORMAT_DATE_LONG);
    extra['requiredOnSiteDate'] = formatDateForServer(
      requiredOnSiteDate?.toString(),
      SERVER_FORMAT_DATE_LONG
    );
    extra['shippingMethodId'] = shippingMethod?.id;
    extra['freightTermId'] = freightTerm?.id;
    extra['billToAddressId'] =
      'addressId' in billToLocation ? billToLocation.addressId : billToLocation?.id;
    extra['billToAddressContactGroupId'] =
      'contactGroup' in billToLocation ? billToLocation.contactGroup?.id : null;
    extra['shipToAddressId'] =
      (shipToLocation && 'addressId' in shipToLocation
        ? shipToLocation.addressId
        : shipToLocation?.id) ?? null;
  }

  if (!isAlternateVersionEnabled) {
    extra['externalCustomerNumber'] = customerNumber?.externalCustomerNumber;
    extra['externalCustomerNumberType'] = customerNumberType || CustomerExternalNumberType.ORDER;
  }

  return {
    purchaseOrderNumber,
    quoteId,
    ...extra,
  };
};

export const mapQuotePartsMoveTo = (
  quoteId: number,
  quoteVersionType: QuoteVersionType,
  quoteParts: ICustomerQuotePart[],
  data: IMoveToForm
) => ({
  quoteId,
  quoteVersionType,
  quoteParts: quoteParts.map((quotePart) => ({ id: quotePart.id })),
  moveTo: data.lineNumber,
});

interface ICostSourcePayload {
  spaCostRegistrationNum?: string;
  spaCostSource?: string;
  spaCostSourceId?: string;
  spaCostSourceName?: string;
  validSince?: string;
  validUntil?: string;
  vendorQuoteNumber?: string;
}

interface ISelectedSpaLinePayload extends IDefaultSpaAgreementPayload {
  costSource: ICostSourcePayload;
  isCostingCustom: number;
  isCostingSelectedSpa: number;
  purchaseCost: number;
}

interface IQuotePartUpdateData {
  accountsPayableId?: number;
  alternateCompanyManufacturerId?: number;
  alternateCompanyManufacturerName?: string;
  id?: number;
  gpm?: number;
  gpmType?: string;
  price?: number;
  priceBasedOn?: string;
  pricingDate?: string;
  pricingStrategy?: PricingStrategy;
  prioritizeLegacyCost?: number;
  isCostingCustom?: number;
  isCostingSelectedSpa?: number;
  isPricingCustom?: number;
  purchaseCost?: number;
  costSource?: ICostSourcePayload;
  replacementCost?: number;
  replacementCostNumber?: string;
  shipmentTypeId?: number;
  corporateLocationId?: number;
}

// Flags are numbers because multipart stringifies JS booleans as "true"/"false".
const mapSelectedSpaLinePayload = (
  selectedSpa: ISelectedSpaFormValue,
  currentCostSource: ICostSourcePayload = {},
  defaultSpaAgreement?: IDefaultSpaAgreementFormValue | null,
  uom?: string | null
): ISelectedSpaLinePayload => ({
  ...mapDefaultSpaAgreementPayload(defaultSpaAgreement, uom),
  costSource: { ...currentCostSource, ...mapSelectedSpaCostSource(selectedSpa) },
  isCostingCustom: 0,
  isCostingSelectedSpa: 1,
  purchaseCost: selectedSpa.spaCost,
});

/**
 * Tri-state: dropping the pick of a saved selected-SPA line is said explicitly, as an agreement can
 * cost exactly the automatic cost. Every other no-pick save omits the key.
 */
const mapSelectedSpaLineChange = (
  selectedSpa: ISelectedSpaFormValue | null | undefined,
  currentCostSource: ICostSourcePayload | undefined,
  wasSelectedSpaLine: boolean,
  defaultSpaAgreement?: IDefaultSpaAgreementFormValue | null,
  uom?: string | null
): Partial<ISelectedSpaLinePayload> => {
  if (selectedSpa?.spaCostSource) {
    return mapSelectedSpaLinePayload(selectedSpa, currentCostSource, defaultSpaAgreement, uom);
  }

  return wasSelectedSpaLine ? { isCostingSelectedSpa: 0 } : {};
};

/**
 * The validity dates the user gave, as the form's fields take them ('' clears): a typed cost's
 * both; a vendor quote cost's Valid until alone, which the BE applies to the quote version's other
 * lines on that vendor quote, left out while empty with nothing to clear. None otherwise: the BE
 * keeps the row's, a system cost carries its own, and a pick's cost source merges over these.
 */
const mapCostValidity = (
  data: Partial<
    Pick<IQuotePartLineForm, 'costSource' | 'isCostingCustom' | 'selectedSpa' | 'vendorQuoteCost'>
  >,
  isAlternateVersionEnabled: boolean,
  savedQuotePart?: TOneTimeSpaRow | null
): Pick<ICostSourcePayload, 'validSince' | 'validUntil'> => {
  const validUntil = formatCostValidityDateForServer(data.costSource?.validUntil);

  switch (getFormCostValidityEntry(data, isAlternateVersionEnabled, savedQuotePart)) {
    case CostValidityEntry.TYPED:
      return {
        validSince: formatCostValidityDateForServer(data.costSource?.validSince),
        validUntil,
      };
    case CostValidityEntry.VENDOR_QUOTE_VALID_UNTIL:
      return validUntil || hasVendorQuoteValidUntilToClear(data.vendorQuoteCost, savedQuotePart)
        ? { validUntil }
        : {};
    default:
      return {};
  }
};

const getMarginValue = (data: ICalculatePriceForm) =>
  -1 === data.margin && data.customMargin ? data.customMargin / 100 : data.margin;

// Bulk path: every selected line gets the same margin (and optionally priceBasedOn).
// No per-line detailed overrides are sent.
export const mapBulkQuotePartsCalculatePricing = (
  quoteParts: ICustomerQuotePart[],
  data: ICalculatePriceForm,
  isAlternateVersionEnabled = false
) => {
  if (!quoteParts.length) {
    return {};
  }

  const marginValue = getMarginValue(data);

  if (!isAlternateVersionEnabled) {
    return {
      quoteParts: quoteParts.map((quotePart) => ({
        id: quotePart.id,
        gpm: marginValue,
        gpmType: PricingHelper.PRICING_TYPE_TOTAL,
      })),
    };
  }

  const priceBasedOn = data?.priceBasedOn || PriceBasedOn.QUOTED;

  return {
    quoteParts: quoteParts
      .filter((quotePart) => !QuotePartHelper.isPriceLocked(quotePart))
      .map((quotePart) => {
        const base = {
          id: quotePart.id,
          gpm: marginValue,
          gpmType: PricingHelper.PRICING_TYPE_TOTAL,
        };

        const dropPriceBasedOn =
          priceBasedOn === PriceBasedOn.REPLACEMENT &&
          !QuotePartHelper.isEligibleForReplacementPricing(quotePart);

        return dropPriceBasedOn ? base : { ...base, priceBasedOn };
      }),
  };
};

// Single-selection path: the targeted quote part (data.id) gets the full pricing payload
// (price, cost overrides, replacement cost, shipment/location, priceBasedOn/pricingDate for
// alternate version). Any additional selected parts get just the margin update.
export const mapSingleQuotePartCalculatePricing = (
  quoteParts: ICustomerQuotePart[],
  data: ICalculatePriceForm,
  isAlternateVersionEnabled = false
) => {
  if (!quoteParts.length) {
    return {};
  }

  const { price } = data;
  const marginValue = getMarginValue(data);

  const firstQuotePart = quoteParts.find((quotePart) => quotePart.id === data.id) ?? quoteParts[0];
  const remainingQuoteParts = quoteParts.filter((quotePart) => quotePart.id !== firstQuotePart.id);
  let firstQuotePartPricing: IQuotePartUpdateData = {
    id: firstQuotePart?.id,
    price: NumberHelper.round(Number(price), firstQuotePart?.quotePriceDecimals),
    replacementCost: data?.replacementCost ?? null,
    replacementCostNumber: data?.replacementCostNumber || null,
    shipmentTypeId: data?.shipmentType?.id,
    corporateLocationId: data?.corporateLocation?.id,
  };

  if (isAlternateVersionEnabled) {
    firstQuotePartPricing = {
      ...firstQuotePartPricing,
      isCostingCustom: data?.isCostingCustom ? 1 : 0,
      prioritizeLegacyCost: data?.prioritizeLegacyCost ? 1 : 0,
      purchaseCost: data?.purchaseCost,
      costSource: {
        vendorQuoteNumber: data?.costSource?.vendorQuoteNumber,
        ...mapCostValidity(data, isAlternateVersionEnabled, firstQuotePart),
      },
      alternateCompanyManufacturerName: data?.companyManufacturerName,
      alternateCompanyManufacturerId: data?.companyManufacturerId,
      accountsPayableId: data?.accountsPayableId,
    };
  }

  if (isAlternateVersionEnabled && !!data?.isPricingCustom) {
    firstQuotePartPricing = {
      ...firstQuotePartPricing,
      isPricingCustom: 1,
    };
  }

  if (isAlternateVersionEnabled && data?.isReplacementCostCustom) {
    // If user has entered a replacement cost, but not a purchase cost, and hit enter without
    // blurring the field
    if (!data?.purchaseCost && !!data?.replacementCost) {
      firstQuotePartPricing = {
        ...firstQuotePartPricing,
        isCostingCustom: data?.isCostingCustom ? 1 : 0,
        prioritizeLegacyCost: data?.prioritizeLegacyCost ? 1 : 0,
        purchaseCost: data?.replacementCost,
        costSource: {
          vendorQuoteNumber: data?.replacementCostNumber,
          ...mapCostValidity(data, isAlternateVersionEnabled, firstQuotePart),
        },
      };
    }
  }

  if (isAlternateVersionEnabled) {
    firstQuotePartPricing = {
      ...firstQuotePartPricing,
      priceBasedOn: data?.priceBasedOn || PriceBasedOn.QUOTED,
      pricingDate:
        formatDateForServer(data?.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
      pricingStrategy: data?.pricingStrategy || null,
    };
  }

  // Last, so a pick wins. Gated on the code: yup casts an absent nullable object to `{}`.
  if (isAlternateVersionEnabled) {
    firstQuotePartPricing = {
      ...firstQuotePartPricing,
      ...mapSelectedSpaLineChange(
        data?.selectedSpa,
        firstQuotePartPricing.costSource,
        null !== getSelectedSpaIdentity(firstQuotePart),
        data?.defaultSpaAgreement,
        firstQuotePart?.uom
      ),
    };
  }

  const remainingQuotePartsPricing = remainingQuoteParts.map((quotePart) => ({
    id: quotePart.id,
    gpm: marginValue,
    gpmType: PricingHelper.PRICING_TYPE_TOTAL,
  }));

  return {
    quoteParts: [firstQuotePartPricing, ...remainingQuotePartsPricing],
  };
};

export const mapQuotePartApplyCostDiscount = (
  quoteParts: ICustomerQuotePart[],
  data: ICostDiscountForm
) => ({
  discount: data.discount,
  quoteParts: quoteParts.map((quotePart) => quotePart.id),
});

export const mapUpsertQuotePartLine = (
  quoteId: number,
  quoteType: QuoteType,
  data: IQuotePartLineForm,
  quotePartId?: number,
  priceDecimals?: number,
  masterQuotePartId = null,
  options: {
    isAlternateVersionEnabled: boolean;
    isTaxonomyStepEnabled: boolean;
    isTaxonomyRequired: boolean;
    hasExternalTaxonomy: boolean;
    isSelectedSpaLine?: boolean;
    // The line as saved, on an edit.
    savedQuotePart?: TOneTimeSpaRow | null;
  } = {
    isAlternateVersionEnabled: false,
    isTaxonomyStepEnabled: false,
    isTaxonomyRequired: false,
    hasExternalTaxonomy: false,
  }
) => {
  const { part, manufacturer, costSource } = data;
  const {
    isTaxonomyStepEnabled,
    isAlternateVersionEnabled,
    isTaxonomyRequired,
    hasExternalTaxonomy,
    isSelectedSpaLine = false,
    savedQuotePart = null,
  } = options;

  let unverifiedPartInfo = {};
  if (!part?.verified) {
    unverifiedPartInfo = {
      description: data.description,
      descriptionLong: data.description,
      source: part?.source ?? Source.INTERNAL,
    };
  }

  let manufacturerId = manufacturer?.id || null;
  if (manufacturer?.isNew) {
    manufacturerId = `__tmp__${manufacturer?.name}`;
  }

  let idInfo = {};
  if (quotePartId) {
    idInfo = { id: quotePartId };
  }

  let alternateInfo = {};
  if (masterQuotePartId) {
    alternateInfo = { masterQuotePartId };
  }

  let taxonomyInfo = {};
  if (isTaxonomyStepEnabled) {
    taxonomyInfo = {
      taxonomyGroupId: data.taxonomy?.id || null,
      taxonomySource: TaxonomySource.MANUAL,
    };
  } else if (isTaxonomyRequired && hasExternalTaxonomy) {
    taxonomyInfo = {
      skipTaxonomyRequiredCheck: 1,
    };
  }

  let costSourceInfo: { costSource?: ICostSourcePayload } = {};
  if (isAlternateVersionEnabled) {
    costSourceInfo = {
      costSource: {
        vendorQuoteNumber: costSource?.vendorQuoteNumber,
        // A vendor quote's own line takes none.
        ...(QuoteType.CUSTOMER === quoteType &&
          mapCostValidity(data, isAlternateVersionEnabled, savedQuotePart)),
      },
    };
  }

  // Spread last, so a pick wins. Gated on the code: yup casts an absent nullable object to `{}`.
  let selectedSpaInfo: Partial<ISelectedSpaLinePayload> = {};
  if (isAlternateVersionEnabled) {
    selectedSpaInfo = mapSelectedSpaLineChange(
      data.selectedSpa,
      costSourceInfo.costSource,
      isSelectedSpaLine,
      data.defaultSpaAgreement,
      data.uom
    );
  }

  let replacementInfo = {};
  if (isAlternateVersionEnabled && part?.supplier?.isShipAndDebit) {
    replacementInfo = {
      replacementCost: data.replacementCost,
      replacementCostNumber: data.replacementCostNumber,
    };
  }

  let vendorInfo = {};
  if (isAlternateVersionEnabled) {
    vendorInfo = {
      alternateCompanyManufacturerName: data.companyManufacturerName,
      alternateCompanyManufacturerId: data.companyManufacturerId,
      accountsPayableId: data.accountsPayableId,
    };
  }

  let priceBasedOnInfo = {};
  if (isAlternateVersionEnabled) {
    priceBasedOnInfo = {
      priceBasedOn: data.priceBasedOn || PriceBasedOn.QUOTED,
      pricingDate:
        formatDateForServer(data.pricingDate?.toString(), SERVER_FORMAT_DATE_LONG) || null,
      pricingStrategy: data.pricingStrategy || null,
    };
  }

  return {
    quotePart: {
      quoteVersionType:
        QuoteType.VENDOR === quoteType ? QuoteVersionType.RELEASE : QuoteVersionType.DRAFT,
      quoteId: quoteId,
      part: part?.number || null,
      manufacturerId,
      vendorNumber: part?.vendorNumber || null,
      uom: data.uom,
      quantity: data.quantity,
      price: data.price,
      notes: data.notes?.map(({ type, content }) => ({ type, content })),
      familyId: part?.familyId || null,
      adjustQuantity: false,
      purchaseCost: data.purchaseCost,
      isCostingCustom: data.isCostingCustom ? 1 : 0,
      isAlternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
      prioritizeLegacyCost: isAlternateVersionEnabled && data.prioritizeLegacyCost ? 1 : 0,
      priceDecimals,
      shipmentTypeId: data.shipmentType?.id,
      corporateLocationId: data.corporateLocation?.id,
      ...unverifiedPartInfo,
      ...idInfo,
      ...alternateInfo,
      ...taxonomyInfo,
      ...costSourceInfo,
      ...replacementInfo,
      ...vendorInfo,
      ...priceBasedOnInfo,
      ...selectedSpaInfo,
    },
  };
};

export const mapQuotePartAddToPricingProfile = (
  quoteParts: ICustomerQuotePart[],
  data: IAddToPricingProfileForm
) => ({
  pricingProfileId: data.pricingProfile.id,
  quoteParts: quoteParts.map((part) => part.id),
  options: {
    price: data?.pricingOptions === QuotePartAddToPricingProfileOption.PRICE ? 1 : 0,
    margin: data?.pricingOptions === QuotePartAddToPricingProfileOption.MARGIN ? 1 : 0,
    cost: data?.cost ? 1 : 0,
  },
});

export const mapQuotePartGetDiscountedParts = (
  data: IAddToPricingProfileForm,
  quoteParts: ICustomerQuotePart[]
): IDiscountPartsFixedPayload => ({
  pricingProfileId: data.pricingProfile.id,
  quoteParts: quoteParts.map((part) => part.id),
});
